import React, { useMemo } from "react";
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { HistoryRange, PacketLossHistoryPoint } from "./requests/request";
import {
    AXIS_LABEL_STYLE,
    AXIS_TICK,
    GRID_STROKE,
    LOSS_COLOR,
    TOOLTIP_CONTENT_STYLE,
    TOOLTIP_LABEL_STYLE,
    formatPointTime,
} from "./historyChart";

interface PacketLossChartProps {
    points: PacketLossHistoryPoint[];
    range: HistoryRange;
}

/** Lost probes per aggregation window (Cisco IP SLA counts, not a percentage). */
const PacketLossChart: React.FC<PacketLossChartProps> = ({ points, range }) => {
    const chartData = useMemo(
        () =>
            points.map((point) => ({
                formattedTime: formatPointTime(point.time, range),
                lost: point.lost,
                samples: point.samples,
            })),
        [points, range]
    );

    /** Keep a flat zero-line readable: never collapse the axis onto itself. */
    const maxLost = useMemo(
        () => chartData.reduce((max, point) => Math.max(max, point.lost ?? 0), 0),
        [chartData]
    );

    return (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart
                data={chartData}
                margin={{ top: 16, right: 24, left: 8, bottom: 8 }}
            >
                <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke={GRID_STROKE}
                />
                <XAxis
                    dataKey="formattedTime"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tick={AXIS_TICK}
                    interval="preserveStartEnd"
                />
                <YAxis
                    domain={[0, Math.max(maxLost, 2)]}
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tick={AXIS_TICK}
                    label={{
                        value: "Packets lost",
                        angle: -90,
                        position: "insideLeft",
                        style: AXIS_LABEL_STYLE,
                    }}
                />
                <Tooltip
                    formatter={(value: number) => [
                        value == null || Number.isNaN(value) ? "—" : value,
                        "Packets lost",
                    ]}
                    contentStyle={TOOLTIP_CONTENT_STYLE}
                    labelStyle={TOOLTIP_LABEL_STYLE}
                />
                <Line
                    type="monotone"
                    dataKey="lost"
                    name="Packets lost"
                    stroke={LOSS_COLOR}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                    connectNulls={false}
                />
            </LineChart>
        </ResponsiveContainer>
    );
};

export default PacketLossChart;
